import { useEffect, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Clapperboard,
  Images,
  LayoutList,
  Library,
  Settings2,
  TriangleAlert,
  Users,
} from "lucide-react";
import type { ItemDeNavegacao } from "@base/ui";
import { useAuth } from "../../../shared/auth/auth.contexto";
import { useTemDashboard } from "../../dashboard/application/use-dashboard";
import { useTemMidias } from "../../midias/application/use-midias";
import {
  useContagemFalhasPublicacoes,
  useTemGerirPublicacoes,
  useTemPublicacoes,
} from "../../publicacoes/application/use-publicacoes";
import { usePodeVerUsuarios } from "../../usuarios/application/use-usuarios";

export type RotaDoApp =
  | "workspace"
  | "admin-clientes"
  | "admin-usuarios"
  | "referencias"
  | "stories"
  | "dashboard"
  | "configuracoes"
  | "perfil"
  | "editorial"
  | "agenda"
  | "falhas-publicacao"
  | "publicados-publicacao"
  | "biblioteca"
  | "mcp"
  | "oauth-consent";

/** Resolve o hash atual para a rota do Creative OS. */
export function resolverRotaDoApp(hash: string): RotaDoApp {
  const limpo = hash.replace(/^#/, "").split("?")[0] ?? "";
  // Clientes virou o seletor de projetos; hashes antigos vao para Configuracoes.
  if (
    limpo === "admin-clientes" ||
    limpo === "gestao-clientes" ||
    limpo === "clientes"
  ) {
    return "configuracoes";
  }
  if (limpo === "admin-usuarios" || limpo === "usuarios") {
    return "admin-usuarios";
  }
  if (limpo === "configuracoes" || limpo === "workspace") {
    return "configuracoes";
  }
  if (limpo === "perfil") return "perfil";
  if (limpo === "dashboard") return "dashboard";
  if (limpo === "editorial") return "editorial";
  if (limpo === "agenda") return "agenda";
  if (limpo === "falhas-publicacao") return "falhas-publicacao";
  if (limpo === "publicados-publicacao") return "publicados-publicacao";
  if (limpo === "biblioteca") return "biblioteca";
  if (limpo === "mcp") return "mcp";
  if (limpo === "oauth/consent") return "oauth-consent";
  if (limpo === "referencias") return "referencias";
  if (limpo === "stories") return "stories";
  if (limpo === "") return "dashboard";
  return "referencias";
}

/** Hash routing do Creative OS. */
export function useRotaDoApp(): RotaDoApp {
  const [rota, definirRota] = useState<RotaDoApp>(() =>
    resolverRotaDoApp(window.location.hash),
  );

  useEffect(() => {
    const aoMudar = () => definirRota(resolverRotaDoApp(window.location.hash));
    window.addEventListener("hashchange", aoMudar);
    return () => window.removeEventListener("hashchange", aoMudar);
  }, []);

  useEffect(() => {
    const limpo = window.location.hash.replace(/^#/, "").split("?")[0] ?? "";
    if (
      limpo === "workspace" ||
      limpo === "admin-clientes" ||
      limpo === "gestao-clientes" ||
      limpo === "clientes"
    ) {
      window.location.hash = "configuracoes";
    }
  }, []);

  return rota;
}

/**
 * Menu lateral: Principal, Conta, Administrativo (admin), Em breve.
 * Projetos ficam no seletor do topo. Popup da conta: Perfil, tema e sair.
 */
export function navegacaoDoApp(
  rota: RotaDoApp,
  opcoes?: {
    readonly mostrarDashboard?: boolean;
    readonly mostrarAgenda?: boolean;
    readonly mostrarFalhas?: boolean;
    readonly badgeFalhas?: number;
    readonly mostrarConfiguracoes?: boolean;
    readonly mostrarReferencias?: boolean;
    readonly mostrarUsuarios?: boolean;
    readonly mostrarBiblioteca?: boolean;
  },
): readonly ItemDeNavegacao[] {
  const itens: ItemDeNavegacao[] = [];

  if (opcoes?.mostrarDashboard) {
    itens.push({
      rotulo: "Dashboard",
      href: "#dashboard",
      icone: <BarChart3 />,
      ativo: rota === "dashboard",
      grupo: "Principal",
    });
  }

  if (opcoes?.mostrarAgenda) {
    const badge =
      opcoes.badgeFalhas && opcoes.badgeFalhas > 0
        ? opcoes.badgeFalhas
        : undefined;
    const filhos: ItemDeNavegacao[] = [
      {
        rotulo: "Publicados",
        href: "#publicados-publicacao",
        icone: <CheckCircle2 />,
        ativo: rota === "publicados-publicacao",
      },
    ];
    if (opcoes.mostrarFalhas) {
      filhos.push({
        rotulo: "Falhas",
        href: "#falhas-publicacao",
        icone: <TriangleAlert />,
        ativo: rota === "falhas-publicacao",
        contador: badge,
      });
    }

    itens.push({
      rotulo: "Agenda",
      href: "#agenda",
      icone: <CalendarDays />,
      ativo:
        rota === "agenda" ||
        rota === "falhas-publicacao" ||
        rota === "publicados-publicacao",
      grupo: "Principal",
      filhos,
    });
  } else if (opcoes?.mostrarFalhas) {
    itens.push({
      rotulo: "Falhas",
      href: "#falhas-publicacao",
      icone: <TriangleAlert />,
      ativo: rota === "falhas-publicacao",
      grupo: "Principal",
      contador:
        opcoes.badgeFalhas && opcoes.badgeFalhas > 0
          ? opcoes.badgeFalhas
          : undefined,
    });
  }

  if (opcoes?.mostrarBiblioteca) {
    itens.push({
      rotulo: "Biblioteca",
      href: "#biblioteca",
      icone: <Library />,
      ativo: rota === "biblioteca",
      grupo: "Principal",
    });
  }

  if (opcoes?.mostrarConfiguracoes !== false) {
    itens.push({
      rotulo: "Configurações",
      href: "#configuracoes",
      icone: <Settings2 />,
      ativo: rota === "configuracoes",
      grupo: "Principal",
    });
  }

  if (opcoes?.mostrarReferencias) {
    // Da conta do usuario: trocar de workspace nao troca estes itens.
    itens.push({
      rotulo: "Referências",
      href: "#referencias",
      icone: <Images />,
      ativo: rota === "referencias",
      grupo: "Conta",
    });
    itens.push({
      rotulo: "Stories",
      href: "#stories",
      icone: <Clapperboard />,
      ativo: rota === "stories",
      grupo: "Conta",
    });
  }

  if (opcoes?.mostrarUsuarios) {
    itens.push({
      rotulo: "Usuários",
      href: "#admin-usuarios",
      icone: <Users />,
      ativo: rota === "admin-usuarios",
      grupo: "Administrativo",
    });
  }

  itens.push({
    rotulo: "Editorial",
    href: "#editorial",
    icone: <LayoutList />,
    ativo: rota === "editorial",
    grupo: "Em breve",
    desabilitado: true,
  });

  return itens;
}

function usePodeVerReferencias(): boolean {
  const { sessao } = useAuth();
  return Boolean(
    sessao?.escopos.includes("referencias:ler") ||
      sessao?.escopos.includes("referencias:escrever"),
  );
}

/** Navegacao lateral com itens condicionados a escopos. */
export function useNavegacaoDoApp(rota: RotaDoApp): readonly ItemDeNavegacao[] {
  const podeDashboard = useTemDashboard();
  const podeAgenda = useTemPublicacoes();
  const podeFalhas = useTemGerirPublicacoes();
  const podeReferencias = usePodeVerReferencias();
  const podeUsuarios = usePodeVerUsuarios();
  const podeBiblioteca = useTemMidias();
  const contagem = useContagemFalhasPublicacoes(podeFalhas);
  return navegacaoDoApp(rota, {
    mostrarDashboard: podeDashboard,
    mostrarAgenda: podeAgenda,
    mostrarFalhas: podeFalhas,
    badgeFalhas: contagem.data?.total ?? 0,
    mostrarConfiguracoes: true,
    mostrarReferencias: podeReferencias,
    mostrarUsuarios: podeUsuarios,
    mostrarBiblioteca: podeBiblioteca,
  });
}

/** Hash inicial apos login (Dashboard quando houver escopo). */
export function rotaInicialPosLogin(
  escopos: readonly string[] | undefined,
): string {
  if (escopos?.includes("dashboard:ler")) return "dashboard";
  return "referencias";
}

/** Apos login, leva ao dashboard (ou referencias) se a URL nao tiver rota. */
export function irParaRotaInicialPosLogin(
  escopos?: readonly string[],
): void {
  const limpo = window.location.hash.replace(/^#/, "").split("?")[0] ?? "";
  if (!limpo) {
    window.location.hash = rotaInicialPosLogin(escopos);
  }
}
